import axios from "axios";
import db from "../../database";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";

const STATUS = { PASS: "pass", FAIL: "fail", WARN: "warn", RUNNING: "running", SKIP: "skip" };

const ACTION_ID = {
  RUN_SYNC: "run-sync",
  REGISTER_TILL: "register-till",
  OPEN_TILL: "open-till",
  RE_LOGIN: "re-login",
};

const makeCheck = (id, name, category, severity, runFn) => ({
  id,
  name,
  category,
  severity,
  status: STATUS.RUNNING,
  detail: "",
  action: null,
  actionId: null,
  run: runFn,
});

const getServerUrl = () =>
  window.__CW_TENANT_URLS__?.REACT_APP_serverUrl || process.env.REACT_APP_serverUrl;

async function checkApiConnectivity() {
  const url = getServerUrl();
  if (!url) return { status: STATUS.FAIL, detail: "No server URL configured", action: "Check environment config" };
  const start = Date.now();
  try {
    const authHeaders = getOAuthHeaders();
    const headers = {};
    if (authHeaders?.access_token) {
      headers.Authorization = authHeaders.access_token;
    }
    headers["Content-Type"] = "application/json";
    await axios.post(url, { query: "{ __typename }" }, { headers, timeout: 10000 });
    const ms = Date.now() - start;
    return { status: STATUS.PASS, detail: `Responded in ${ms}ms` };
  } catch (err) {
    const ms = Date.now() - start;
    if (err.code === "ECONNABORTED" || ms > 9500) {
      return { status: STATUS.FAIL, detail: `Timeout after ${ms}ms`, action: "Check network connectivity" };
    }
    if (err.response) {
      const code = err.response.status;
      if (code === 401 || code === 403) {
        return { status: STATUS.WARN, detail: `Auth error (${code}) — API reachable but token may be expired`, action: "Re-login", actionId: ACTION_ID.RE_LOGIN };
      }
      return { status: STATUS.PASS, detail: `API reachable (HTTP ${code}, ${ms}ms)` };
    }
    return { status: STATUS.FAIL, detail: `Connection failed: ${err.message}`, action: "Check network / VPN" };
  }
}

function checkAuthToken() {
  const authHeaders = getOAuthHeaders();
  if (!authHeaders || !authHeaders.access_token || authHeaders.access_token === "bearer undefined") {
    return { status: STATUS.FAIL, detail: "No access token found", action: "Login required", actionId: ACTION_ID.RE_LOGIN };
  }
  if (!authHeaders.email && !authHeaders.username) {
    return { status: STATUS.WARN, detail: "Token exists but no user identity", action: "Re-login", actionId: ACTION_ID.RE_LOGIN };
  }
  return { status: STATUS.PASS, detail: `User: ${authHeaders.email || authHeaders.username}` };
}

function checkTillAssignment() {
  const tillData = useAuthStore.getState().tillData;
  if (!tillData) {
    return { status: STATUS.FAIL, detail: "tillData is null — backend may be down or user not configured", action: "Contact admin to assign till" };
  }
  if (!tillData.tillAccess) {
    return { status: STATUS.FAIL, detail: "tillAccess is null — no till assigned to this user (causes NPE on backend)", action: "Assign CWR Till to user in admin" };
  }
  const tillName = tillData.tillAccess?.cwrTill?.till || "Unknown";
  const bunitName = tillData.tillAccess?.csBunit?.name || "Unknown";
  return { status: STATUS.PASS, detail: `Till: ${tillName} | Store: ${bunitName}` };
}

async function checkTillSession() {
  try {
    const events = await db.tillEvents.toArray();
    const openEvent = events.find((e) => e.tillStatus === "open");
    if (openEvent) {
      return { status: STATUS.PASS, detail: `Till open (session: ${openEvent.tillSessionId?.substring(0, 8)}...)` };
    }
    if (events.length > 0) {
      return { status: STATUS.WARN, detail: "Till events exist but none open", action: "Open Till", actionId: ACTION_ID.OPEN_TILL };
    }
    return { status: STATUS.WARN, detail: "No till events in database", action: "Open Till to start session", actionId: ACTION_ID.OPEN_TILL };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}`, action: "Check database" };
  }
}

function checkDefaultCustomer() {
  const tillData = useAuthStore.getState().tillData;
  const b2c = tillData?.tillAccess?.csBunit?.b2cCustomer;
  if (!b2c) {
    return { status: STATUS.FAIL, detail: "No B2C default customer configured — billing will fail", action: "Configure B2C customer in admin" };
  }
  return { status: STATUS.PASS, detail: `Default: ${b2c.name || b2c.code || "Set"}` };
}

function checkPaymentMethods() {
  const tillData = useAuthStore.getState().tillData;
  const payments = tillData?.tillAccess?.csBunit?.paymentMethodList;
  if (!payments || payments.length === 0) {
    return { status: STATUS.FAIL, detail: "No payment methods configured", action: "Configure payment methods in admin" };
  }
  const names = payments.slice(0, 5).map((p) => p.name).join(", ");
  return { status: STATUS.PASS, detail: `${payments.length} methods: ${names}` };
}

async function checkProductsSync() {
  try {
    const count = await db.products.count();
    if (count === 0) {
      return { status: STATUS.FAIL, detail: "0 products — run initial sync", action: "Run Sync", actionId: ACTION_ID.RUN_SYNC };
    }
    return { status: STATUS.PASS, detail: `${count.toLocaleString()} products` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}`, action: "Check database" };
  }
}

async function checkUomSync() {
  try {
    const count = await db.productUom.count();
    if (count === 0) {
      return { status: STATUS.WARN, detail: "0 UOMs — weight-based products may fail", action: "Run Sync", actionId: ACTION_ID.RUN_SYNC };
    }
    return { status: STATUS.PASS, detail: `${count.toLocaleString()} UOMs` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

async function checkPricingRulesSync() {
  try {
    const count = await db.pricingRules.count();
    if (count === 0) {
      return { status: STATUS.WARN, detail: "0 pricing rules — promotions disabled" };
    }
    return { status: STATUS.PASS, detail: `${count.toLocaleString()} rules` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

async function checkSaleTypesSync() {
  try {
    const count = await db.posSaletypes.count();
    if (count === 0) {
      return { status: STATUS.FAIL, detail: "0 sale types — payment modal won't work", action: "Run Sync", actionId: ACTION_ID.RUN_SYNC };
    }
    return { status: STATUS.PASS, detail: `${count.toLocaleString()} sale types` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

async function checkLastSync() {
  try {
    const summaries = await db.dataSyncSummary.toArray();
    if (summaries.length === 0) {
      return { status: STATUS.FAIL, detail: "Never synced", action: "Run initial sync", actionId: ACTION_ID.RUN_SYNC };
    }
    const latest = summaries.sort((a, b) => (b.syncId || "").localeCompare(a.syncId || ""))[0];
    const syncTime = latest.syncEndTime || latest.syncStartTime;
    if (syncTime) {
      const parsed = new Date(syncTime).getTime();
      if (isNaN(parsed)) {
        return { status: STATUS.WARN, detail: "Sync time unparseable", action: "Run Sync", actionId: ACTION_ID.RUN_SYNC };
      }
      const hrs = Math.round((Date.now() - parsed) / 3600000);
      if (hrs > 24) {
        return { status: STATUS.WARN, detail: `Last sync: ${hrs}h ago — consider re-sync`, action: "Run Sync", actionId: ACTION_ID.RUN_SYNC };
      }
      return { status: STATUS.PASS, detail: `Last sync: ${hrs}h ago` };
    }
    return { status: STATUS.PASS, detail: `${summaries.length} sync records` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

async function checkPrinterConnectivity() {
  const posConfig = usePOSConfigStore.getState().posConfig;
  const printerUrl = posConfig?.hardwareConfig?.printerURL || "http://127.0.0.1:8070";
  if (posConfig?.printReceipt === "N") {
    return { status: STATUS.SKIP, detail: "Printing disabled in config" };
  }
  try {
    await axios.get(printerUrl, { timeout: 3000 });
    return { status: STATUS.PASS, detail: `Printer at ${printerUrl}` };
  } catch (err) {
    if (err.response) {
      return { status: STATUS.PASS, detail: `Printer reachable (HTTP ${err.response.status})` };
    }
    return { status: STATUS.WARN, detail: `Printer offline at ${printerUrl}`, action: "Start printer service" };
  }
}

async function checkDatabaseAccess() {
  try {
    await db.products.count();
    const mode = window.electronAPI?.db ? "SQLite" : "IndexedDB";
    return { status: STATUS.PASS, detail: `${mode} — accessible` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `Database error: ${err.message}`, action: "Clear browser data and re-sync" };
  }
}

function checkLocalStorage() {
  const critical = ["tillData", "userData"];
  const missing = [];
  const corrupt = [];
  for (const key of critical) {
    const val = localStorage.getItem(key);
    if (!val) {
      missing.push(key);
      continue;
    }
    try {
      JSON.parse(val);
    } catch {
      corrupt.push(key);
    }
  }
  if (corrupt.length > 0) {
    return { status: STATUS.FAIL, detail: `Corrupt: ${corrupt.join(", ")}`, action: "Clear localStorage and re-login" };
  }
  if (missing.length > 0) {
    return { status: STATUS.WARN, detail: `Missing: ${missing.join(", ")}` };
  }
  return { status: STATUS.PASS, detail: "Critical keys present and valid" };
}

async function checkPendingOrders() {
  try {
    const orders = await db.orders.toArray();
    const pending = orders.filter((o) => o.isSynced === "N" || o.isSynced === false);
    if (pending.length === 0) {
      return { status: STATUS.PASS, detail: "0 pending" };
    }
    if (pending.length > 10) {
      return { status: STATUS.WARN, detail: `${pending.length} orders pending sync — network may be down`, action: "Check network" };
    }
    return { status: STATUS.PASS, detail: `${pending.length} pending sync` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

async function checkStorageQuota() {
  if (window.electronAPI) {
    return { status: STATUS.SKIP, detail: "N/A in CW Browser mode" };
  }
  if (!navigator.storage?.estimate) {
    return { status: STATUS.SKIP, detail: "Storage API not available" };
  }
  try {
    const est = await navigator.storage.estimate();
    const usedMB = Math.round((est.usage || 0) / 1048576);
    const quotaMB = Math.round((est.quota || 0) / 1048576);
    const pct = quotaMB > 0 ? Math.round((usedMB / quotaMB) * 100) : 0;
    if (pct > 80) {
      return { status: STATUS.WARN, detail: `${usedMB} MB / ${quotaMB} MB (${pct}%)`, action: "Clear old data" };
    }
    return { status: STATUS.PASS, detail: `${usedMB} MB / ${quotaMB} MB (${pct}%)` };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `Error: ${err.message}` };
  }
}

async function checkProductDataQuality() {
  try {
    const count = await db.products.count();
    if (count === 0) return { status: STATUS.SKIP, detail: "No products to check" };
    const products = await db.products.limit(500).toArray();
    const issues = [];
    const zeroPriceProducts = products.filter((p) => {
      const pl = p.priceList || [];
      return pl.length > 0 && pl.every((e) => Number(e.pricestd || 0) === 0) && p.productSegment !== "GC" && p.productSegment !== "CN";
    });
    if (zeroPriceProducts.length > 0) {
      issues.push(`${zeroPriceProducts.length} products with zero price (unsellable)`);
    }
    const zeroTaxWithCategory = products.filter((p) => p.taxCategory && p.taxCategory !== "NoTax" && Number(p.taxRate || 0) === 0);
    if (zeroTaxWithCategory.length > 0) {
      issues.push(`${zeroTaxWithCategory.length} products: tax category set but taxRate=0`);
    }
    const allZeroStock = products.every((p) => Number(p.onhandQty || 0) === 0);
    if (allZeroStock && products.length > 5) {
      issues.push("All products have 0 stock — stock data may not be synced");
    }
    if (issues.length === 0) return { status: STATUS.PASS, detail: `${products.length} products — data looks clean` };
    if (issues.some((i) => i.includes("zero price") || i.includes("taxRate=0"))) {
      return { status: STATUS.WARN, detail: issues.join("; "), action: "Verify product setup in backend" };
    }
    return { status: STATUS.WARN, detail: issues.join("; ") };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

async function checkTillRegistration() {
  try {
    const regs = await db.tillRegistrations.toArray();
    if (regs.length === 0) return { status: STATUS.WARN, detail: "No till registration — till not yet registered", action: "Register Till", actionId: ACTION_ID.REGISTER_TILL };
    const latest = regs[regs.length - 1];
    const status = latest.status || "unknown";
    if (status === "Success" || latest.access_token) {
      return { status: STATUS.PASS, detail: `Registered (${regs.length} record${regs.length > 1 ? "s" : ""})` };
    }
    return { status: STATUS.WARN, detail: `Registration status: ${status}`, action: "Re-register till", actionId: ACTION_ID.REGISTER_TILL };
  } catch (err) {
    return { status: STATUS.FAIL, detail: `DB error: ${err.message}` };
  }
}

function checkNetworkStatus() {
  const posConfig = usePOSConfigStore.getState().posConfig;
  const isOfflineDisabled = posConfig?.disableOfflineBilling === "Y";
  if (!navigator.onLine) {
    if (isOfflineDisabled) {
      return { status: STATUS.FAIL, detail: "Offline — billing disabled (disableOfflineBilling=Y)", action: "Restore network" };
    }
    return { status: STATUS.WARN, detail: "Offline — offline billing available" };
  }
  return { status: STATUS.PASS, detail: "Online" };
}

export function buildChecks() {
  return [
    makeCheck("api", "API Connectivity", "Backend", "critical", checkApiConnectivity),
    makeCheck("auth", "Authentication", "Backend", "critical", checkAuthToken),
    makeCheck("network", "Network Status", "Backend", "critical", checkNetworkStatus),
    makeCheck("till-assign", "Till Assignment", "Till & Session", "critical", checkTillAssignment),
    makeCheck("till-session", "Till Session", "Till & Session", "critical", checkTillSession),
    makeCheck("b2c-customer", "Default Customer", "Till & Session", "high", checkDefaultCustomer),
    makeCheck("payment-methods", "Payment Methods", "Till & Session", "critical", checkPaymentMethods),
    makeCheck("products", "Products", "Sync Data", "critical", checkProductsSync),
    makeCheck("uom", "UOM Data", "Sync Data", "high", checkUomSync),
    makeCheck("pricing-rules", "Pricing Rules", "Sync Data", "medium", checkPricingRulesSync),
    makeCheck("sale-types", "Sale Types", "Sync Data", "critical", checkSaleTypesSync),
    makeCheck("last-sync", "Last Sync", "Sync Data", "high", checkLastSync),
    makeCheck("data-quality", "Product Data Quality", "Sync Data", "medium", checkProductDataQuality),
    makeCheck("till-reg", "Till Registration", "Till & Session", "high", checkTillRegistration),
    makeCheck("printer", "Printer", "Hardware", "high", checkPrinterConnectivity),
    makeCheck("db", "Database", "Storage", "critical", checkDatabaseAccess),
    makeCheck("localstorage", "Local Storage", "Storage", "high", checkLocalStorage),
    makeCheck("pending-orders", "Pending Orders", "Storage", "medium", checkPendingOrders),
    makeCheck("storage-quota", "Storage Quota", "Storage", "low", checkStorageQuota),
  ];
}

export { STATUS, ACTION_ID };
